import { defineStore } from 'pinia'
import { ref } from 'vue'
import { getAllWorkflows, getWorkflowConfig } from '../service/workflows'

function transformWorkflowToXStateConfig(workflow: any) {
  if (!workflow) return null

  const states: any = {}

  if (workflow.initialStatus) {
    states[workflow.initialStatus.name] = { on: {} }
  }

  if (Array.isArray(workflow.transitions)) {
    workflow.transitions.forEach((transition: any) => {
      const fromStatusName = transition.fromStatus?.name
      const toStatusName = transition.toStatus?.name

      if (!fromStatusName || !toStatusName) return

      if (!states[fromStatusName]) {
        states[fromStatusName] = { on: {} }
      }

      if (!states[toStatusName]) {
        states[toStatusName] = { on: {} }
      }

      states[fromStatusName].on[transition.name] = {
        target: toStatusName,
        actions: transition.actions || [],
      }
    })
  }

  return {
    id: workflow.id,
    name: workflow.name,
    initial: workflow.initialStatus?.name || 'Open',
    states,
  }
}

export const useWorkflowStore = defineStore('workflow', () => {
  const workflows = ref<any[]>([])

  const workflowConfigs = ref<Map<string, any>>(new Map())
  const isLoadingWorkflows = ref(false)
  const loadingWorkflowIds = ref<Set<string>>(new Set())

  const loadWorkflow = async (id: string) => {
    if (workflowConfigs.value.has(id)) {
      return workflowConfigs.value.get(id)
    }

    if (loadingWorkflowIds.value.has(id)) {
      while (loadingWorkflowIds.value.has(id)) {
        await new Promise((resolve) => setTimeout(resolve, 50))
      }

      return workflowConfigs.value.get(id)
    }

    loadingWorkflowIds.value.add(id)

    const response = await getWorkflowConfig(id)

    return new Promise<any>((resolve, reject) => {
      response.onSuccess((result: any) => {
        workflowConfigs.value.set(id, result)
        loadingWorkflowIds.value.delete(id)
        resolve(result)
      })
      response.onError((error: any) => {
        console.error(`Error on workflow "${id}":`, error)
        loadingWorkflowIds.value.delete(id)
        reject(error)
      })
    })
  }

  const getWorkflow = (id: string) => {
    return workflowConfigs.value.get(id) || null
  }

  const loadAllWorkflows = async () => {
    const response = await getAllWorkflows()

    return new Promise<void>((resolve, reject) => {
      response.onSuccess((result: any) => {
        workflows.value = result
        resolve()
      })
      response.onError((error: any) => {
        console.error('Error loading all workflows:', error)
        reject(error)
      })
    })
  }

  const loadAllWorkflowConfigs = async () => {
    if (isLoadingWorkflows.value) return

    isLoadingWorkflows.value = true

    try {
      await loadAllWorkflows()

      workflows.value.forEach((workflow: any) => {
        const config = transformWorkflowToXStateConfig(workflow)

        if (config) {
          workflowConfigs.value.set(workflow.id, config)
        }
      })
    } finally {
      isLoadingWorkflows.value = false
    }
  }

  const getWorkflowByTypeId = (typeId: string) => {
    return (
      workflows.value.find(
        (workflow: any) => Array.isArray(workflow.ticketType) && workflow.ticketType.some((tt: any) => tt.id === typeId),
      ) || null
    )
  }

  const getWorkflowById = (id: string) => {
    return workflows.value.find((workflow: any) => workflow.id === id) || null
  }

  return {
    workflows,
    workflowConfigs,
    isLoadingWorkflows,
    loadWorkflow,
    getWorkflow,
    loadAllWorkflows,
    loadAllWorkflowConfigs,
    getWorkflowByTypeId,
    getWorkflowById,
  }
})
